/**
 * axon AMBIENT FORM
 * Weather and environmental data entry
 * @production-ready
 * @version 4.0.0 - FIXED
 */

import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Cloud, Edit } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  UniversalTable,
  useTableData,
  useTableNavigation,
  useAutoScroll,
  DEFAULT_STYLE_CONFIG,
  EXPANDED_STYLE_CONFIG
} from '../../../universal_table';
import { usePersistentTableData } from '../../../universal_table/hooks/usePersistentTableData';
import { FormActions, StatusBanner, SummaryModal, CorrectMistakeModal, DateFilter, DateRangeFilter } from '../../../shared';
import { AMBIENT_COLUMNS } from '../forms_data/ambientColumns';
import { ambientService, AmbientDataRow, generalService, loadAllPaginatedData } from '../services/axonApiService';
import { useAuth } from '../../../../../../context/AuthContext';
import backendApi from '../../../../../../services/backendApi';
import { resolveUserFilterId, shouldLoadAllRecords } from '../utils/userDataAccess';
import { buildExcelUploadStorageKey, buildUserScopedStorageKey } from '../utils/storageKeys';
import { buildGenerationReportFooterText } from '../utils/generationReportFooter';
import { addRemovedRow, filterRemovedRows, syncRemovedRowsWithBackend, removeRemovedRow, clearRemovedRows } from '../utils/removedRowsManager';
import { DeleteRowDialog } from '../components/DeleteRowDialog';

interface AmbientFormProps {
  site?: string;
}

export const AmbientForm: React.FC<AmbientFormProps> = ({ site: siteProp }) => {
  const AMB_DEBUG = false; // ⚡ Set true for debug logging, false for production speed
  const { site: siteFromParams } = useParams<{ site: string }>();
  const site = siteProp || siteFromParams;
  const siteCode = (site || 'axon').toLowerCase();
  const [isCondensed, setIsCondensed] = useState(true);
  const [status, setStatus] = useState<any>({ type: null, message: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<AmbientDataRow[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showCorrectMistake, setShowCorrectMistake] = useState(false);
  const [correctMistakeData, setCorrectMistakeData] = useState<AmbientDataRow[]>([]);
  const [loadingCorrectMistake, setLoadingCorrectMistake] = useState(false);
  const [siteName, setSiteName] = useState<string>('axon');
  // Range Filter State
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<{ index: number; key: string } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const isManuallySortingRef = useRef(false);
  const [tableKey, setTableKey] = useState(0); // Force re-render key
  const previousRowKeysRef = useRef<string[]>([]); // Track previous rowKeys to detect changes
  const justAddedRowRef = useRef<string | null>(null); // Track newly added row to prevent real-time refresh from overwriting
  const hasScrolledToBottomRef = useRef(false); // Track if we've scrolled to bottom on initial load
  const [isTableVisible, setIsTableVisible] = useState(false); // Hide table until scroll is positioned
  const oldestLoadedDateRef = useRef<string | null>(null); // Track oldest date we've loaded for lazy loading
  const isLoadingMoreRef = useRef(false); // Track if we're currently loading more data
  const hasMoreDataRef = useRef(true); // Track if there's more data to load
  const SCROLL_TOP_LOAD_THRESHOLD = 260;
  const persistentStorageKey = useMemo(
    () => buildUserScopedStorageKey('unsaved_ambient', siteCode, user),
    [siteCode, user?.username, user?.email, user?.fullName, user?.siteId]
  );

  const { data, rowKeys: hookRowKeys, updateCell, removeRow, setAllData } = usePersistentTableData(
    {},
    [],
    { storageKey: persistentStorageKey, enabled: true, debounceMs: 500 }
  );
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  // Latest-value ref so effects (e.g. the real-time poll below) can read current
  // `data` without listing it as a dependency — avoids tearing down and re-arming
  // setInterval/setTimeout timers on every single keystroke. (See `rowKeysRef` below,
  // defined once the local `rowKeys` state exists.)
  const dataRef = useRef(data);
  dataRef.current = data;

  // Track tab visibility to gate polling/refresh work
  useEffect(() => {
    const handleVisibility = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Local state to track rowKeys for immediate updates
  const [rowKeys, setRowKeysLocal] = useState<string[]>(hookRowKeys);
  const rowKeysRef = useRef(rowKeys);
  rowKeysRef.current = rowKeys;

  const footerText = useMemo(
    () => buildGenerationReportFooterText({
      siteName,
      sheetName: 'Weather',
      dateKeys: rowKeys
    }),
    [siteName, rowKeys]
  );

  // Sync local state with hook state (except when manually sorting)
  useEffect(() => {
    if (!isManuallySortingRef.current) {
      setRowKeysLocal(hookRowKeys);
      previousRowKeysRef.current = hookRowKeys;
    }
  }, [hookRowKeys]);

  // Track rowKeys changes and force re-render if order changed
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) {
      previousRowKeysRef.current = rowKeys;
      return;
    }

    // Check if the order changed (not just the content)
    const orderChanged = JSON.stringify(rowKeys) !== JSON.stringify(previousRowKeysRef.current);
    if (orderChanged && !isManuallySortingRef.current) {
      AMB_DEBUG && console.log('🔄 Detected rowKeys order change, forcing re-render');
      setTableKey(prev => prev + 1);
    }
    previousRowKeysRef.current = rowKeys;
  }, [rowKeys, isInitialLoad]);

  // Memoize rowKeys to ensure new reference when order changes
  const memoizedRowKeys = useMemo(() => {
    return Array.from(rowKeys); // Create new array reference
  }, [rowKeys.join(',')]); // Depend on the stringified version to detect order changes

  // Filter rowKeys by selected date range
  const filteredRowKeys = useMemo(() => {
    if (!startDate && !endDate) return rowKeys;

    return rowKeys.filter(key => {
      const rowDate = String(data[key]?.entry_date || key).split('T')[0];

      // If only start date is selected
      if (startDate && !endDate) {
        return rowDate >= startDate;
      }

      // If only end date is selected
      if (!startDate && endDate) {
        return rowDate <= endDate;
      }

      // If both are selected
      if (startDate && endDate) {
        return rowDate >= startDate && rowDate <= endDate;
      }

      return true;
    });
  }, [rowKeys, data, startDate, endDate]);

  // Load data from backend when date filter changes (Weather sheet optimization)
  useEffect(() => {
    if (!startDate && !endDate) return; // No filter applied
    if (isInitialLoad) return; // Don't trigger during initial load

    let isCancelled = false;

    const loadFilteredData = async () => {
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id || isCancelled) return;

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

        AMB_DEBUG && console.log(`🔍 [Weather] Loading filtered data: ${startDate} to ${endDate}`);

        const rows = await loadAllPaginatedData(
          (params) => ambientService.loadByDateRange({
            userId: userIdForLoad,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            startDate: params.startDate,
            endDate: params.endDate,
            offset: params.offset,
            limit: params.limit
          }) as any,
          {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            initialLimit: 200
          }
        );

        if (isCancelled) return;

        if (rows.length > 0) {
          let loadedData: any = {};
          let loadedKeys: string[] = [];

          rows.forEach((row: any) => {
            const dateKey = row.entry_date;
            if (dateKey) {
              const mappedRow: any = {
                entry_date: dateKey,
                id: row.id || row.pk || undefined
              };
              AMBIENT_COLUMNS.forEach(col => {
                if (col.key !== 'entry_date') {
                  const value = row[col.key];
                  mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
                }
              });
              loadedData[dateKey] = mappedRow;
              if (!loadedKeys.includes(dateKey)) {
                loadedKeys.push(dateKey);
              }
            }
          });

          loadedKeys.sort();
          setAllData(loadedData, loadedKeys);
          AMB_DEBUG && console.log(`✅ [Weather] Loaded ${loadedKeys.length} filtered records`);
        } else {
          setAllData({}, []);
        }
      } catch (error) {
        console.error('❌ [Weather] Error loading filtered data:', error);
      }
    };

    loadFilteredData();
    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate]);
  const { scrollToCell, registerInput } = useAutoScroll(containerRef);
  const { focusedCell, setFocusedCell, handleKeyDown } = useTableNavigation(
    rowKeys.length, AMBIENT_COLUMNS.length, AMBIENT_COLUMNS, scrollToCell
  );
  // Stable reference so memoized table rows aren't forced to re-render just because
  // this closure was recreated (setFocusedCell itself is already stable).
  const handleTableFocus = useCallback(
    (r: number, c: number) => setFocusedCell({ rowIndex: r, colIndex: c }),
    [setFocusedCell]
  );

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, siteCode.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === siteCode.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(siteCode.toUpperCase());
          }
        }
      } catch (error) {
        AMB_DEBUG && console.warn('Failed to fetch site name:', error);
        setSiteName(siteCode.toUpperCase());
      }
    };

    fetchSiteName();
  }, [siteCode]);

  /**
   * Create a new empty row with all fields initialized
   */
  const createEmptyRow = (date: string) => {
    return {
      entry_date: date,
      sunrise_time: '',
      sunset_time: '',
      uptime_minutes: '',
      ghi_kwh_per_m2: '',
      poa_kwh_per_m2: '',
      avg_ambient_temp_c: '',
      max_ambient_temp_c: '',
      avg_module_temp_c: '',
      max_module_temp_c: '',
      avg_air_speed_ms: '',
      max_air_speed_ms: '',
      weather_status: ''
    };
  };

  /**
   * Initialize by loading user's existing data or Excel uploaded data
   * IMPORTANT: Check for persisted unsaved data first to preserve user's work
   * OPTIMIZED: Check sessionStorage cache to prevent reloading when switching sheets
   */
  useEffect(() => {
    if (!isInitialLoad) return;

    const loadUserData = async () => {
      try {
        // Clear temporary removals on initial page load so they reappear after refresh
        backendApi.getCurrentUser().then(response => {
          if (response.success && response.user?.id) {
            const backendUser = response.user;
            const resolvedUserId = resolveUserFilterId(user, backendUser);
            const userIdForStore = resolvedUserId ?? Number(backendUser.id);
            clearRemovedRows('Ambient', siteCode, userIdForStore);
            AMB_DEBUG && console.log('🧹 Cleared temporary removals on page load for Ambient');
          } else {
            clearRemovedRows('Ambient', siteCode);
            AMB_DEBUG && console.log('🧹 Cleared temporary removals on page load for Ambient');
          }
        }).catch(() => {
          clearRemovedRows('Ambient', siteCode);
          AMB_DEBUG && console.log('🧹 Cleared temporary removals on page load for Ambient');
        });

        // OPTIMIZATION: Check sessionStorage cache first to avoid reloading when switching sheets
        const cacheKey = `ambient_cache_${siteCode}_${user?.username || 'anonymous'}`;
        const cachedDataStr = sessionStorage.getItem(cacheKey);

        if (cachedDataStr) {
          try {
            const cachedData = JSON.parse(cachedDataStr);
            const cacheTimestamp = cachedData.timestamp || 0;
            const cacheAge = Date.now() - cacheTimestamp;
            const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

            // Use cache if it's less than 5 minutes old and has data
            if (cacheAge < CACHE_MAX_AGE && cachedData.data && cachedData.rowKeys && cachedData.rowKeys.length > 0) {
              AMB_DEBUG && console.log(`⚡ [CACHE HIT] Using cached Ambient data (${cachedData.rowKeys.length} rows, ${Math.round(cacheAge / 1000)}s old)`);
              setAllData(cachedData.data, cachedData.rowKeys);
              oldestLoadedDateRef.current = cachedData.oldestLoadedDate || null;
              hasMoreDataRef.current = cachedData.hasMoreData !== false;
              setIsInitialLoad(false);
              return;
            } else {
              AMB_DEBUG && console.log(`🗑️ [CACHE EXPIRED] Cache is ${Math.round(cacheAge / 1000)}s old, reloading...`);
              sessionStorage.removeItem(cacheKey);
            }
          } catch (e) {
            AMB_DEBUG && console.warn('Failed to parse cached data:', e);
            sessionStorage.removeItem(cacheKey);
          }
        }

        // First, check if persisted data was already restored by usePersistentTableData hook
        // Only use persisted data if it has actual meaningful data (not just empty rows)
        // BUT: Always load from backend to ensure we have the latest data with IDs
        // Persisted data is mainly for unsaved changes, not for replacing backend data
        const hasPersistedData = rowKeys.length > 0;

        if (hasPersistedData) {
          AMB_DEBUG && console.log(`ℹ️ Found persisted Ambient data (${rowKeys.length} rows), but will load from backend to ensure latest data`);
          // Don't return - continue to load from backend
          // The backend data will be merged with persisted data if needed
        }

        // Second, check for Excel uploaded data
        if (site) {
          const storageKey = buildExcelUploadStorageKey(site, user);
          const excelDataStr = localStorage.getItem(storageKey);

          if (excelDataStr) {
            try {
              const excelData = JSON.parse(excelDataStr);
              const ambientData = excelData['Ambient'];

              if (ambientData && Object.keys(ambientData).length > 0) {
                const loadedData: any = {};
                const allKeys: string[] = [];

                Object.entries(ambientData).forEach(([dateKey, rowData]: [string, any]) => {
                  loadedData[dateKey] = {
                    entry_date: dateKey,
                    sunrise_time: rowData.sunrise_time || '',
                    sunset_time: rowData.sunset_time || '',
                    uptime_minutes: rowData.uptime_minutes || '',
                    ghi_kwh_per_m2: rowData.ghi_kwh_per_m2 || '',
                    poa_kwh_per_m2: rowData.poa_kwh_per_m2 || '',
                    avg_ambient_temp_c: rowData.avg_ambient_temp_c || '',
                    max_ambient_temp_c: rowData.max_ambient_temp_c || '',
                    avg_module_temp_c: rowData.avg_module_temp_c || '',
                    max_module_temp_c: rowData.max_module_temp_c || '',
                    avg_air_speed_ms: rowData.avg_air_speed_ms || '',
                    max_air_speed_ms: rowData.max_air_speed_ms || '',
                    weather_status: rowData.weather_status || ''
                  };
                  // Add all dates (including today) to rowKeys
                  if (!allKeys.includes(dateKey)) {
                    allKeys.push(dateKey);
                  }
                });

                // Sort all dates (ascending)
                allKeys.sort();

                // Final rowKeys: all data sorted
                const finalKeys = [...allKeys];

                // Filter out removed rows
                const userResponse = await backendApi.getCurrentUser();
                let userIdForFilter: number | undefined;
                if (userResponse.success && userResponse.user?.id) {
                  const backendUser = userResponse.user;
                  const resolvedUserId = resolveUserFilterId(user, backendUser);
                  userIdForFilter = resolvedUserId ?? Number(backendUser.id);
                }
                // Sync removed rows with backend - restore any rows that exist in backend
                syncRemovedRowsWithBackend(finalKeys, 'Ambient', siteCode, userIdForFilter);

                // Filter out removed rows (persists across sheet switches)
                // Rows that exist in backend will be automatically restored
                const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
                  loadedData,
                  finalKeys,
                  'Ambient',
                  siteCode,
                  userIdForFilter
                );

                setAllData(filteredData, filteredKeys);
                localStorage.removeItem(storageKey);
                setStatus({ type: 'success', message: `✅ Loaded ${filteredKeys.length} rows from Excel upload (${allKeys.length - filteredKeys.length} removed from view)` });
                AMB_DEBUG && console.log(`✅ Loaded ${filteredKeys.length} rows from Excel for Ambient (${allKeys.length - filteredKeys.length} removed)`);
                setIsInitialLoad(false);
                return;
              }
            } catch (excelError) {
              console.error('Error parsing Excel data:', excelError);
            }
          }
        }

        // If no Excel data, load from backend
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

          if (resolvedUserId === null && userIdForLoad === 0) {
            AMB_DEBUG && console.warn('⚠️ Unable to resolve Ambient user ID - defaulting to admin view (all data)');
          }

          // ⚡ SUPERFAST: Single API call with 2-year range covers ALL historical data
          const isAdmin = shouldLoadAllRecords(user, backendUser);
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - 730); // 2 years - ensures ALL data found on FIRST call
          const start = startDate.toISOString().split('T')[0];
          const end = endDate.toISOString().split('T')[0];

          AMB_DEBUG && console.log(`📥 [AMB] Loading for ${userIdForLoad <= 0 ? 'admin' : `user ${userIdForLoad}`} (${start} to ${end})`);

          let loadedData: any = {};
          let loadedKeys: string[] = [];

          if (isAdmin) {
            // Admin: Load only latest data using get-dgr endpoint (LAZY LOADING)
            const API_BASE_URL = 'http://localhost:8000/api/v1';

            try {
              const token = sessionStorage.getItem('access_token');
              const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Ambient&limit=500`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const payload = await response.json();
                const ambientData = payload?.data?.ambient || [];

                ambientData.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined,
                      entry_date: dateKey,
                      sunrise_time: row.sunrise_time || '',
                      sunset_time: row.sunset_time || '',
                      uptime_minutes: row.uptime_minutes ? String(row.uptime_minutes) : '',
                      ghi_kwh_per_m2: row.ghi_kwh_per_m2 ? String(row.ghi_kwh_per_m2) : '',
                      poa_kwh_per_m2: row.poa_kwh_per_m2 ? String(row.poa_kwh_per_m2) : '',
                      avg_ambient_temp_c: row.avg_ambient_temp_c ? String(row.avg_ambient_temp_c) : '',
                      max_ambient_temp_c: row.max_ambient_temp_c ? String(row.max_ambient_temp_c) : '',
                      avg_module_temp_c: row.avg_module_temp_c ? String(row.avg_module_temp_c) : '',
                      max_module_temp_c: row.max_module_temp_c ? String(row.max_module_temp_c) : '',
                      avg_air_speed_ms: row.avg_air_speed_ms ? String(row.avg_air_speed_ms) : '',
                      max_air_speed_ms: row.max_air_speed_ms ? String(row.max_air_speed_ms) : '',
                      weather_status: row.weather_status ? String(row.weather_status).trim() : ''
                    };
                    if (!loadedKeys.includes(dateKey)) {
                      loadedKeys.push(dateKey);
                    }
                  }
                });

                // Show ALL returned dates for initial load (backend limit=500 handles pagination)
                if (loadedKeys.length > 0) {
                  loadedKeys.sort();
                  const finalKeys = [...loadedKeys].sort();

                  // Track oldest date we've loaded for lazy loading
                  if (finalKeys.length > 0) {
                    oldestLoadedDateRef.current = finalKeys[0];
                    hasMoreDataRef.current = true; // Always true initially
                    AMB_DEBUG && console.log(`📦 [LAZY LOADING] Admin initial load: ${finalKeys.length} dates (all returned), oldest: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
                  }

                  loadedKeys = finalKeys;
                }
              }
            } catch (error) {
              console.error('Error loading data via get-dgr:', error);
            }
          } else {
            // Regular user: Load only latest data (LAZY LOADING)
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const authHeaders: HeadersInit = {};
            if (token) authHeaders['Authorization'] = `Bearer ${token}`;
            const response = await fetch(`${API_BASE_URL}/ambient/range/?start_date=${start}&end_date=${end}`, { headers: authHeaders });

            if (response.ok) {
              const payload = await response.json();
              const ambientData = Array.isArray(payload) ? payload : (payload?.data || []);

              // Filter by user
              const userData = ambientData.filter((row: any) => {
                const rowUserId = row.user_id || row.user?.id;
                return rowUserId === userIdForLoad;
              });

              userData.forEach((row: any) => {
                const dateKey = row.entry_date;
                if (dateKey) {
                  loadedData[dateKey] = {
                    id: row.id || row.pk || undefined,
                    entry_date: dateKey,
                    sunrise_time: row.sunrise_time || '',
                    sunset_time: row.sunset_time || '',
                    uptime_minutes: row.uptime_minutes ? String(row.uptime_minutes) : '',
                    ghi_kwh_per_m2: row.ghi_kwh_per_m2 ? String(row.ghi_kwh_per_m2) : '',
                    poa_kwh_per_m2: row.poa_kwh_per_m2 ? String(row.poa_kwh_per_m2) : '',
                    avg_ambient_temp_c: row.avg_ambient_temp_c ? String(row.avg_ambient_temp_c) : '',
                    max_ambient_temp_c: row.max_ambient_temp_c ? String(row.max_ambient_temp_c) : '',
                    avg_module_temp_c: row.avg_module_temp_c ? String(row.avg_module_temp_c) : '',
                    max_module_temp_c: row.max_module_temp_c ? String(row.max_module_temp_c) : '',
                    avg_air_speed_ms: row.avg_air_speed_ms ? String(row.avg_air_speed_ms) : '',
                    max_air_speed_ms: row.max_air_speed_ms ? String(row.max_air_speed_ms) : '',
                    weather_status: row.weather_status ? String(row.weather_status).trim() : ''
                  };
                  if (!loadedKeys.includes(dateKey)) {
                    loadedKeys.push(dateKey);
                  }
                }
              });

              // Show ALL returned dates for initial load (365-day window covers all historical data)
              if (loadedKeys.length > 0) {
                loadedKeys.sort();
                const finalKeys = [...loadedKeys].sort();

                // Track oldest date we've loaded for lazy loading
                if (finalKeys.length > 0) {
                  oldestLoadedDateRef.current = finalKeys[0];
                  hasMoreDataRef.current = true; // Always true initially
                  AMB_DEBUG && console.log(`📦 [LAZY LOADING] User initial load: ${finalKeys.length} dates (all returned), oldest: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
                }

                loadedKeys = finalKeys;
              }
            }
          }

          if (Object.keys(loadedData).length > 0) {
            // Sort all dates (ascending)
            loadedKeys.sort();
            const finalKeys = [...loadedKeys];

            // Sync removed rows with backend - restore any rows that exist in backend
            const userIdForFilter = resolvedUserId ?? Number(backendUser.id);
            syncRemovedRowsWithBackend(finalKeys, 'Ambient', siteCode, userIdForFilter);

            // Filter out removed rows (persists across sheet switches)
            // Rows that exist in backend will be automatically restored
            const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
              loadedData,
              finalKeys,
              'Ambient',
              siteCode,
              userIdForFilter,
              { restoreBackendRows: true } // Restore backend rows on initial load since we cleared temporary removals
            );

            AMB_DEBUG && console.log(`📋 Setting data with ${filteredKeys.length} rowKeys (${finalKeys.length - filteredKeys.length} removed):`, filteredKeys.slice(0, 5), filteredKeys.length > 5 ? '...' : '');
            AMB_DEBUG && console.log(`📋 Loaded data keys:`, Object.keys(filteredData).slice(0, 5));
            if (filteredKeys.length > 0) {
              AMB_DEBUG && console.log(`📋 First row data:`, filteredData[filteredKeys[0]]);
            }

            // Clear persisted data first to avoid conflicts, then set backend data
            try {
              localStorage.removeItem(persistentStorageKey);
              AMB_DEBUG && console.log('🧹 Cleared persisted data to avoid conflicts');
            } catch (e) {
              AMB_DEBUG && console.warn('Failed to clear persisted data:', e);
            }

            setAllData(filteredData, filteredKeys);

            // OPTIMIZATION: Cache the loaded data in sessionStorage
            try {
              const cacheData = {
                data: filteredData,
                rowKeys: filteredKeys,
                oldestLoadedDate: oldestLoadedDateRef.current,
                hasMoreData: hasMoreDataRef.current,
                timestamp: Date.now()
              };
              sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
              AMB_DEBUG && console.log(`💾 [CACHE] Cached Ambient data (${filteredKeys.length} rows)`);
            } catch (e) {
              AMB_DEBUG && console.warn('Failed to cache data:', e);
            }

            AMB_DEBUG && console.log(
              `✅ [INITIAL LOAD] Loaded ${filteredKeys.length} records (latest 10 dates) for ${userIdForLoad <= 0 ? 'all users (admin view)' : `user ${userIdForLoad}`
              }`
            );
          } else {
            // No existing data
            setAllData({}, []);
            AMB_DEBUG && console.log(`✅ No existing data`);
          }
        } else {
          // User not found
          setAllData({}, []);
          AMB_DEBUG && console.log(`✅ User not found`);
        }
      } catch (error: any) {
        console.error('❌ Error loading user data:', error);
        // On error, set empty data
        setAllData({}, []);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadUserData();
  }, [isInitialLoad, setAllData, site]);

  /**
   * Load more older data when user scrolls up (lazy loading)
   */
  const loadMoreOlderData = async () => {
    if (isLoadingMoreRef.current || !hasMoreDataRef.current || !oldestLoadedDateRef.current) {
      return;
    }

    isLoadingMoreRef.current = true;
    AMB_DEBUG && console.log(`📦 [LAZY LOADING] Loading more Ambient data older than ${oldestLoadedDateRef.current}`);

    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        isLoadingMoreRef.current = false;
        return;
      }

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

      // Fetch 30 days before oldest loaded date for lazy loading
      const endDate = new Date(oldestLoadedDateRef.current);
      endDate.setDate(endDate.getDate() - 1); // One day before oldest loaded date
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days back for substantial lazy load chunks
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      let loadedData: any = {};
      let loadedKeys: string[] = [];

      // Backend handles role-based access; fetch all pages for the requested range.
      const rows = await loadAllPaginatedData(
        (params) => ambientService.loadByDateRange({
          userId: userIdForLoad,
          siteCode: siteCode,
          siteId: user?.siteId || backendUser?.site?.site_id || null,
          startDate: params.startDate,
          endDate: params.endDate,
          offset: params.offset,
          limit: params.limit
        }) as any,
        {
          startDate: start,
          endDate: end,
          initialLimit: 200
        }
      );

      rows.forEach((row: any) => {
        const dateKey = row.entry_date;
        if (dateKey && dateKey < oldestLoadedDateRef.current!) {
          loadedData[dateKey] = {
            id: row.id || row.pk || undefined,
            entry_date: dateKey,
            sunrise_time: row.sunrise_time || '',
            sunset_time: row.sunset_time || '',
            uptime_minutes: row.uptime_minutes ? String(row.uptime_minutes) : '',
            ghi_kwh_per_m2: row.ghi_kwh_per_m2 ? String(row.ghi_kwh_per_m2) : '',
            poa_kwh_per_m2: row.poa_kwh_per_m2 ? String(row.poa_kwh_per_m2) : '',
            avg_ambient_temp_c: row.avg_ambient_temp_c ? String(row.avg_ambient_temp_c) : '',
            max_ambient_temp_c: row.max_ambient_temp_c ? String(row.max_ambient_temp_c) : '',
            avg_module_temp_c: row.avg_module_temp_c ? String(row.avg_module_temp_c) : '',
            max_module_temp_c: row.max_module_temp_c ? String(row.max_module_temp_c) : '',
            avg_air_speed_ms: row.avg_air_speed_ms ? String(row.avg_air_speed_ms) : '',
            max_air_speed_ms: row.max_air_speed_ms ? String(row.max_air_speed_ms) : '',
            weather_status: row.weather_status ? String(row.weather_status).trim() : ''
          };
          if (!loadedKeys.includes(dateKey)) {
            loadedKeys.push(dateKey);
          }
        }
      });

      if (loadedKeys.length > 0) {
        // Take ALL newly loaded dates (no artificial cap)
        loadedKeys.sort();
        const newKeysToAdd = loadedKeys;

        // SCROLL PRESERVATION: Save scroll state before prepending data
        const container = containerRef.current;
        const prevScrollHeight = container ? container.scrollHeight : 0;
        const prevScrollTop = container ? container.scrollTop : 0;

        // Merge with existing data — read via refs (not the `data`/`rowKeys` closure
        // values) so callers of this function stay correct even if invoked from an
        // effect that doesn't re-run on every keystroke (see scroll/wheel effect below).
        const currentData = { ...dataRef.current };
        const currentKeys = [...rowKeysRef.current];

        newKeysToAdd.forEach(key => {
          if (!currentKeys.includes(key) && loadedData[key]) {
            currentData[key] = loadedData[key];
            currentKeys.push(key);
          }
        });

        // Sort all keys and update oldest date
        currentKeys.sort();
        oldestLoadedDateRef.current = currentKeys[0];

        // OPTIMIZATION: Update cache with new data
        try {
          const cacheKey = `ambient_cache_${siteCode}_${user?.username || 'anonymous'}`;
          const cacheData = {
            data: currentData,
            rowKeys: currentKeys,
            oldestLoadedDate: oldestLoadedDateRef.current,
            hasMoreData: hasMoreDataRef.current,
            timestamp: Date.now()
          };
          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (e) {
          AMB_DEBUG && console.warn('Failed to update cache:', e);
        }

        // There might be more data as long as we got any records back
        hasMoreDataRef.current = true;

        // Update data
        setAllData(currentData, currentKeys);

        // SCROLL PRESERVATION: Restore scroll position after DOM updates
        requestAnimationFrame(() => {
          if (containerRef.current) {
            const newScrollHeight = containerRef.current.scrollHeight;
            const scrollDiff = newScrollHeight - prevScrollHeight;
            containerRef.current.scrollTop = prevScrollTop + scrollDiff;
          }
        });

        AMB_DEBUG && console.log(`📦 [LAZY LOADING] Loaded ${newKeysToAdd.length} older Ambient dates, oldest now: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
      } else {
        // No more data
        hasMoreDataRef.current = false;
        AMB_DEBUG && console.log(`📦 [LAZY LOADING] No more Ambient data to load`);
      }
    } catch (error) {
      console.error('Error loading more Ambient data:', error);
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  /**
   * Real-time data refresh - Poll database every 30 seconds for fresh data
   */
  useEffect(() => {
    if (isInitialLoad) return; // Don't poll during initial load
    if (justAddedRowRef.current) {
      AMB_DEBUG && console.log(`⏸️ [REAL-TIME] Skipping refresh - new row just added: ${justAddedRowRef.current}`);
      return; // Don't refresh if a new row was just added
    }

    const loadFreshData = async () => {
      if (document.hidden) return;
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id) return;

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
        const isAdmin = shouldLoadAllRecords(user, backendUser);

        let loadedData: any = {};
        let allKeys: string[] = [];

        if (isAdmin) {
          try {
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Ambient`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const payload = await response.json();
              const ambientData = payload?.data?.ambient || [];

              ambientData.forEach((row: any) => {
                const dateKey = row.entry_date;
                if (dateKey) {
                  loadedData[dateKey] = {
                    entry_date: dateKey,
                    id: row.id || row.pk || undefined,
                    sunrise_time: row.sunrise_time || '',
                    sunset_time: row.sunset_time || '',
                    uptime_minutes: row.uptime_minutes || '',
                    ghi_kwh_per_m2: row.ghi_kwh_per_m2 || '',
                    poa_kwh_per_m2: row.poa_kwh_per_m2 || '',
                    avg_ambient_temp_c: row.avg_ambient_temp_c || '',
                    max_ambient_temp_c: row.max_ambient_temp_c || '',
                    avg_module_temp_c: row.avg_module_temp_c || '',
                    max_module_temp_c: row.max_module_temp_c || '',
                    avg_air_speed_ms: row.avg_air_speed_ms || '',
                    max_air_speed_ms: row.max_air_speed_ms || '',
                    weather_status: row.weather_status || ''
                  };
                  if (!allKeys.includes(dateKey)) {
                    allKeys.push(dateKey);
                  }
                }
              });
            }
          } catch (error) {
            console.error('Error refreshing Ambient data via get-dgr:', error);
            return;
          }
        } else {
          const loadResult = await ambientService.loadByUser(userIdForLoad);
          if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
            loadResult.data.forEach((row: any) => {
              const dateKey = row.entry_date;
              if (dateKey) {
                loadedData[dateKey] = {
                  entry_date: dateKey,
                  id: row.id || row.pk || undefined,
                  sunrise_time: row.sunrise_time || '',
                  sunset_time: row.sunset_time || '',
                  uptime_minutes: row.uptime_minutes || '',
                  ghi_kwh_per_m2: row.ghi_kwh_per_m2 || '',
                  poa_kwh_per_m2: row.poa_kwh_per_m2 || '',
                  avg_ambient_temp_c: row.avg_ambient_temp_c || '',
                  max_ambient_temp_c: row.max_ambient_temp_c || '',
                  avg_module_temp_c: row.avg_module_temp_c || '',
                  max_module_temp_c: row.max_module_temp_c || '',
                  avg_air_speed_ms: row.avg_air_speed_ms || '',
                  max_air_speed_ms: row.max_air_speed_ms || '',
                  weather_status: row.weather_status || ''
                };
                if (!allKeys.includes(dateKey)) {
                  allKeys.push(dateKey);
                }
              }
            });
          }
        }

        allKeys.sort();
        const finalKeys = [...allKeys];

        if (Object.keys(loadedData).length > 0) {
          const userIdForFilter = resolvedUserId ?? Number(backendUser.id);

          // Sync removed rows with backend - restore any rows that exist in backend
          syncRemovedRowsWithBackend(finalKeys, 'Ambient', siteCode, userIdForFilter);

          // Filter out removed rows (persists across sheet switches)
          // Rows that exist in backend will be automatically restored
          const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
            loadedData,
            finalKeys,
            'Ambient',
            siteCode,
            userIdForFilter
          );

          // Merge fresh data with current data to preserve unsaved rows.
          // Read via refs (not the `data`/`rowKeys` closure values) so this effect
          // doesn't need `data`/`rowKeys` in its dependency array — see refs above.
          const currentData = dataRef.current;
          const currentRowKeys = rowKeysRef.current;
          const mergedData = { ...currentData }; // Start with current data (includes unsaved rows)
          const mergedKeys: string[] = []; // Will build merged keys list

          // Track which keys are from database
          const dbKeysSet = new Set(filteredKeys);

          // First, add all rows from database (these are saved)
          filteredKeys.forEach(key => {
            if (filteredData[key]) {
              mergedData[key] = filteredData[key];
              mergedKeys.push(key);
            }
          });

          // Then, preserve all rows that exist in current data but not in database (unsaved rows)
          currentRowKeys.forEach(key => {
            if (!dbKeysSet.has(key) && currentData[key]) {
              // This is an unsaved row (exists locally but not in DB), preserve it
              mergedData[key] = currentData[key];
              if (!mergedKeys.includes(key)) {
                mergedKeys.push(key);
              }
              AMB_DEBUG && console.log(`💾 [REAL-TIME] Preserving unsaved Ambient row: ${key}`);
            }
          });

          // Sort merged keys
          mergedKeys.sort();

          const currentDataSignature = JSON.stringify(Object.keys(currentData).sort());
          const newDataSignature = JSON.stringify(Object.keys(mergedData).sort());

          if (currentDataSignature !== newDataSignature ||
            JSON.stringify([...currentRowKeys].sort()) !== JSON.stringify(mergedKeys.sort())) {
            AMB_DEBUG && console.log(`🔄 [REAL-TIME] Refreshed Ambient: ${mergedKeys.length} records (preserved ${currentRowKeys.length - filteredKeys.length} unsaved rows)`);
            setAllData(mergedData, mergedKeys);
          }
        }
      } catch (error) {
        console.error('Error refreshing Ambient data:', error);
      }
    };

    const intervalId = isTabVisible ? setInterval(loadFreshData, 30000) : null;
    const timeoutId = isTabVisible ? setTimeout(loadFreshData, 5000) : null;

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
    // `data`/`rowKeys` intentionally excluded — read via dataRef/rowKeysRef above so
    // this effect doesn't tear down and re-arm its 30s/5s timers on every keystroke.
  }, [isInitialLoad, site, siteCode, user, setAllData, isTabVisible]);

  /**
   * Auto-sort rows by date (ascending - oldest to newest, so newest appears at bottom)
   */
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) return;

    const sortRows = () => {
      if (isManuallySortingRef.current) return;

      const sortedKeys = [...rowKeys].sort();

      // Compare stringified arrays to avoid reference comparison issues
      const isAlreadySorted = JSON.stringify(sortedKeys) === JSON.stringify(rowKeys);

      if (!isAlreadySorted) {
        AMB_DEBUG && console.log('🔄 Auto-sorting rows by date', { current: rowKeys, expected: sortedKeys });
        isManuallySortingRef.current = true;
        setTimeout(() => {
          setRowKeysLocal(sortedKeys);
          setTableKey(prev => prev + 1);
          setTimeout(() => {
            isManuallySortingRef.current = false;
          }, 100);
        }, 0);
      }
    };

    // If manual sorting is in progress, wait for it to complete, then sort
    if (isManuallySortingRef.current) {
      const timeoutId = setTimeout(() => {
        sortRows();
      }, 150);
      return () => clearTimeout(timeoutId);
    }

    // Otherwise, sort immediately
    sortRows();
    // `data`/`setAllData` were listed here but never read in this effect — they only
    // forced a re-sort-check on every keystroke (which changes `data`, not `rowKeys`).
  }, [rowKeys, isInitialLoad]);

  // Reset scroll flag when initial load starts (so we can position when it completes)
  useEffect(() => {
    if (isInitialLoad) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false); // Hide table during initial load
    }
  }, [isInitialLoad]);

  // Reset scroll flag when tableKey changes (table re-rendered due to sorting, need to reposition)
  useEffect(() => {
    if (tableKey > 0 && !isInitialLoad && rowKeys.length > 0 && !isManuallySortingRef.current) {
      // Only reset if we're not in the middle of manual sorting
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false); // Hide table when it re-renders so we can reposition
    }
  }, [tableKey, isInitialLoad, rowKeys.length]);

  // Lazy loading: Detect scroll up and load more older data
  // Uses BOTH scroll + wheel events to handle cases where content doesn't fill viewport
  useEffect(() => {
    if (isInitialLoad || !containerRef.current) return;

    const container = containerRef.current;
    let scrollTimeout: ReturnType<typeof setTimeout>;
    let wheelTimeout: ReturnType<typeof setTimeout>;

    const triggerLoadMore = () => {
      if (!containerRef.current || isLoadingMoreRef.current || !hasMoreDataRef.current) return;
      loadMoreOlderData();
    };

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!containerRef.current || isLoadingMoreRef.current || !hasMoreDataRef.current) return;
        const scrollTop = container.scrollTop;
        if (scrollTop <= SCROLL_TOP_LOAD_THRESHOLD) {
          triggerLoadMore();
        }
      }, 150);
    };

    // Wheel event fires even when content doesn't overflow (not scrollable)
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY >= 0) return; // Only react to scroll-up (deltaY < 0)
      if (isLoadingMoreRef.current || !hasMoreDataRef.current) return;
      const scrollTop = container.scrollTop;
      // Trigger if near top OR if content doesn't fill viewport (not scrollable)
      const isNotScrollable = container.scrollHeight <= container.clientHeight;
      if (scrollTop <= SCROLL_TOP_LOAD_THRESHOLD || isNotScrollable) {
        clearTimeout(wheelTimeout);
        wheelTimeout = setTimeout(() => triggerLoadMore(), 150);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });

    // Auto-fill: if content doesn't fill viewport after load, load more immediately
    const isNotScrollable = container.scrollHeight <= container.clientHeight;
    if (isNotScrollable && hasMoreDataRef.current && !isLoadingMoreRef.current && rowKeys.length > 0) {
      triggerLoadMore();
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      clearTimeout(scrollTimeout);
      clearTimeout(wheelTimeout);
    };
    // `data` intentionally excluded: `loadMoreOlderData` now reads current data/rowKeys
    // via refs, so this effect only needs to re-attach listeners when the container
    // becomes available or the row count changes, not on every keystroke.
  }, [isInitialLoad, rowKeys.length]);

  // Set initial scroll position to bottom SYNCHRONOUSLY before browser paints
  // This ensures the sheet starts directly from bottom rows - user never sees top rows
  useLayoutEffect(() => {
    // Wait for data to be available, initial load to complete, and sorting to finish
    if (isInitialLoad || !containerRef.current || isManuallySortingRef.current) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false); // Hide table during initial load or sorting
      return;
    }

    // If no data, just show the empty table with headers
    if (rowKeys.length === 0) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(true);
      return;
    }

    // If we've already set the position, don't do it again
    if (hasScrolledToBottomRef.current) return;

    const container = containerRef.current;

    // Check if ALL rows are rendered in the DOM
    const tbody = container.querySelector('tbody');
    const actualRowCount = tbody ? tbody.querySelectorAll('tr').length : 0;
    const expectedRowCount = rowKeys.length;

    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    // CRITICAL: Check if rows are rendered (even if just 1 row, we want to position at bottom)
    const rowsRendered = actualRowCount > 0;
    const hasScrollableContent = maxScroll > 0 && scrollHeight > clientHeight;

    // If we have rows rendered, always position to bottom (even if not scrollable, position at 0 which is bottom for small content)
    if (rowsRendered) {
      // Disable smooth scrolling to prevent animation (set permanently)
      container.style.scrollBehavior = 'auto';

      // Set scroll position directly to bottom IMMEDIATELY (synchronously, before paint)
      // Use maxScroll if scrollable, otherwise 0 (which shows bottom for small content)
      const targetScroll = hasScrollableContent ? maxScroll : 0;
      container.scrollTop = targetScroll;

      // Force immediate update by reading scrollTop (triggers layout recalculation)
      const currentScroll = container.scrollTop;

      // Verify position was set correctly (allow some tolerance)
      if (Math.abs(currentScroll - targetScroll) < 1 || currentScroll >= targetScroll - 5 || (!hasScrollableContent && currentScroll === 0)) {
        hasScrolledToBottomRef.current = true;

        // Show table only after scroll is positioned correctly
        setIsTableVisible(true);

        AMB_DEBUG && console.log('⬇️ [Ambient] useLayoutEffect: Set position to bottom (sheet starts from bottom rows)', {
          scrollHeight,
          clientHeight,
          maxScroll,
          targetScroll,
          actualScrollTop: currentScroll,
          expectedRowCount,
          actualRowCount,
          rowsRendered,
          hasScrollableContent
        });
      } else {
        // If position wasn't set correctly, try again in next frame
        requestAnimationFrame(() => {
          if (containerRef.current && !hasScrolledToBottomRef.current) {
            const retryMaxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
            const retryTarget = retryMaxScroll > 0 ? retryMaxScroll : 0;
            containerRef.current.scrollTop = retryTarget;
            const retryScroll = containerRef.current.scrollTop;
            if (Math.abs(retryScroll - retryTarget) < 1 || retryScroll >= retryTarget - 5 || (!hasScrollableContent && retryScroll === 0)) {
              hasScrolledToBottomRef.current = true;
              setIsTableVisible(true);
            }
          }
        });
      }
    }
  }, [isInitialLoad, rowKeys.length, tableKey]);

  // Fallback useEffect for cases where useLayoutEffect runs before rows are fully rendered
  useEffect(() => {
    // Wait for data to be available and initial load to complete
    if (isInitialLoad || rowKeys.length === 0 || !containerRef.current) {
      return;
    }

    // If we've already set the position, don't do it again
    if (hasScrolledToBottomRef.current) return;

    const container = containerRef.current;
    const expectedRowCount = rowKeys.length;

    const setPositionToBottom = () => {
      if (!containerRef.current || hasScrolledToBottomRef.current) return;

      const container = containerRef.current;

      // Check if all rows are actually rendered in the DOM
      const tbody = container.querySelector('tbody');
      const actualRowCount = tbody ? tbody.querySelectorAll('tr').length : 0;

      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      // Check if rows are rendered
      const rowsRendered = actualRowCount > 0;
      const hasScrollableContent = maxScroll > 0 && scrollHeight > clientHeight;

      if (rowsRendered) {
        // Disable smooth scrolling to prevent animation (set permanently)
        container.style.scrollBehavior = 'auto';

        // Set scroll position directly to bottom immediately (no animation)
        // Use maxScroll if scrollable, otherwise 0 (which shows bottom for small content)
        const targetScroll = hasScrollableContent ? maxScroll : 0;
        container.scrollTop = targetScroll;

        // Force immediate update by reading scrollTop
        const currentScroll = container.scrollTop;

        // Verify position was set correctly (allow some tolerance)
        if (Math.abs(currentScroll - targetScroll) < 1 || currentScroll >= targetScroll - 5 || (!hasScrollableContent && currentScroll === 0)) {
          hasScrolledToBottomRef.current = true;

          // Show table only after scroll is positioned correctly
          setIsTableVisible(true);

          AMB_DEBUG && console.log('⬇️ [Ambient] useEffect fallback: Set position to bottom (sheet starts from bottom rows)', {
            scrollHeight,
            clientHeight,
            maxScroll,
            targetScroll,
            actualScrollTop: container.scrollTop,
            expectedRowCount,
            actualRowCount,
            hasScrollableContent
          });
        }
      }
    };

    // Use MutationObserver to watch for DOM changes and set position when table updates
    const observer = new MutationObserver(() => {
      if (!hasScrolledToBottomRef.current && containerRef.current) {
        setPositionToBottom();
      }
    });

    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
    }

    // Try immediately
    setPositionToBottom();

    // Try with requestAnimationFrame
    requestAnimationFrame(() => {
      setPositionToBottom();
      requestAnimationFrame(() => {
        setPositionToBottom();
      });
    });

    // Try after various delays to catch different render stages
    const timeouts = [
      setTimeout(setPositionToBottom, 0),
      setTimeout(setPositionToBottom, 50),
      setTimeout(setPositionToBottom, 100),
      setTimeout(setPositionToBottom, 200),
      setTimeout(setPositionToBottom, 300),
      setTimeout(setPositionToBottom, 500),
      setTimeout(setPositionToBottom, 800)
    ];

    return () => {
      observer.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, [isInitialLoad, rowKeys.length, tableKey]);

  /**
   * Add new row with automatic next date
   * Also checks database for existing data for this date and loads it if found
   */
  const handleAddRow = async () => {
    let newDate: string;

    if (rowKeys.length > 0) {
      // Get the latest date and add 1 day
      const sortedDates = [...rowKeys].sort();
      const latestDate = sortedDates[sortedDates.length - 1];
      const nextDay = new Date(latestDate);
      nextDay.setDate(nextDay.getDate() + 1);
      newDate = nextDay.toISOString().split('T')[0];
    } else {
      // No dates exist, use today
      newDate = new Date().toISOString().split('T')[0];
    }

    // Check if this date already exists
    if (rowKeys.includes(newDate)) {
      setStatus({
        type: 'warning',
        message: `⚠️ Date ${newDate} already exists. Please edit existing row or delete it.`
      });

      // Focus on existing row
      const existingIndex = rowKeys.indexOf(newDate);
      setFocusedCell({ rowIndex: existingIndex, colIndex: 0 });
      setTimeout(() => scrollToCell(existingIndex, 0), 100);
      return;
    }

    AMB_DEBUG && console.log(`➕ Adding new row with date: ${newDate}`);

    // Mark this row as just added to prevent real-time refresh from overwriting it
    justAddedRowRef.current = newDate;

    // Check if data exists in database for this date
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (userResponse.success && userResponse.user?.id) {
        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
        const isAdmin = shouldLoadAllRecords(user, backendUser);

        // Load data for this specific date
        let existingDataForDate: any = null;

        if (isAdmin) {
          // Admin: Use get-dgr endpoint for the specific date
          try {
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Ambient`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const payload = await response.json();
              const ambientData = payload?.data?.ambient || [];
              // Find data for this date (Ambient has one row per date)
              existingDataForDate = ambientData.find((row: any) => row.entry_date === newDate);
            }
          } catch (error) {
            AMB_DEBUG && console.warn('Error loading data for date via get-dgr:', error);
          }
        } else {
          // Regular user: Load their data and find by date
          const loadResult = await ambientService.loadByUser(userIdForLoad);
          if (loadResult.ok && loadResult.data) {
            existingDataForDate = loadResult.data.find((row: any) => row.entry_date === newDate);
          }
        }

        // If data exists, populate row with that data
        if (existingDataForDate) {
          AMB_DEBUG && console.log(`📥 Found existing record for date ${newDate}, loading data...`);

          const updatedData = {
            ...data,
            [newDate]: {
              id: existingDataForDate.id || existingDataForDate.pk || undefined,
              entry_date: newDate,
              sunrise_time: existingDataForDate.sunrise_time || '',
              sunset_time: existingDataForDate.sunset_time || '',
              uptime_minutes: existingDataForDate.uptime_minutes ? String(existingDataForDate.uptime_minutes) : '',
              ghi_kwh_per_m2: existingDataForDate.ghi_kwh_per_m2 ? String(existingDataForDate.ghi_kwh_per_m2) : '',
              poa_kwh_per_m2: existingDataForDate.poa_kwh_per_m2 ? String(existingDataForDate.poa_kwh_per_m2) : '',
              avg_ambient_temp_c: existingDataForDate.avg_ambient_temp_c ? String(existingDataForDate.avg_ambient_temp_c) : '',
              max_ambient_temp_c: existingDataForDate.max_ambient_temp_c ? String(existingDataForDate.max_ambient_temp_c) : '',
              avg_module_temp_c: existingDataForDate.avg_module_temp_c ? String(existingDataForDate.avg_module_temp_c) : '',
              max_module_temp_c: existingDataForDate.max_module_temp_c ? String(existingDataForDate.max_module_temp_c) : '',
              avg_air_speed_ms: existingDataForDate.avg_air_speed_ms ? String(existingDataForDate.avg_air_speed_ms) : '',
              max_air_speed_ms: existingDataForDate.max_air_speed_ms ? String(existingDataForDate.max_air_speed_ms) : '',
              weather_status: existingDataForDate.weather_status || ''
            }
          };

          // Add new date and sort all dates
          const updatedKeys = [...rowKeys, newDate].sort();

          // Update local state
          setTimeout(() => {
            setRowKeysLocal(updatedKeys);
            setTableKey(prev => prev + 1);
            setAllData(updatedData, updatedKeys);
          }, 0);

          setStatus({
            type: 'success',
            message: `✅ Loaded existing data for ${newDate}`
          });

          // Focus on new row
          setTimeout(() => {
            const newIndex = updatedKeys.indexOf(newDate);
            setFocusedCell({ rowIndex: newIndex, colIndex: 1 }); // Focus first editable column
            scrollToCell(newIndex, 1);
          }, 150);

          // Clear the flag after a delay
          setTimeout(() => {
            justAddedRowRef.current = null;
          }, 10000);

          return;
        }
      }
    } catch (error) {
      AMB_DEBUG && console.warn('Error checking database for existing data:', error);
      // Continue with creating empty row if check fails
    }

    // No data found in database, create empty row
    AMB_DEBUG && console.log(`📝 No existing data found for ${newDate}, creating empty row`);

    // Create new row with date populated
    const updatedData = {
      ...data,
      [newDate]: createEmptyRow(newDate)
    };

    // Add new date and sort all dates
    const updatedKeys = [...rowKeys, newDate].sort();

    setTimeout(() => {
      setRowKeysLocal(updatedKeys);
      setTableKey(prev => prev + 1);
      setAllData(updatedData, updatedKeys);
    }, 0);

    // Clear the flag after a delay to allow real-time refresh to resume
    setTimeout(() => {
      justAddedRowRef.current = null;
    }, 10000); // 10 seconds should be enough for user to start editing

    // Focus on new row
    setTimeout(() => {
      const newIndex = updatedKeys.indexOf(newDate);
      setFocusedCell({ rowIndex: newIndex, colIndex: 1 }); // Focus first editable column
      scrollToCell(newIndex, 1);
    }, 150);
  };

  /**
   * Load existing data for a specific date from database
   */
  const loadDataForDate = async (date: string): Promise<any | null> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return null;
      }

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
      const isAdmin = shouldLoadAllRecords(user, backendUser);

      let existingDataForDate: any = null;

      if (isAdmin) {
        // Admin: Use get-dgr endpoint
        try {
          const API_BASE_URL = 'http://localhost:8000/api/v1';
          const token = sessionStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Ambient`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const payload = await response.json();
            const ambientData = payload?.data?.ambient || [];
            existingDataForDate = ambientData.find((row: any) => row.entry_date === date);
          }
        } catch (error) {
          AMB_DEBUG && console.warn('Error loading data for date via get-dgr:', error);
        }
      } else {
        // Regular user: Load their data and find by date
        const loadResult = await ambientService.loadByUser(userIdForLoad);
        if (loadResult.ok && loadResult.data) {
          existingDataForDate = loadResult.data.find((row: any) => row.entry_date === date);
        }
      }

      return existingDataForDate || null;
    } catch (error) {
      console.error('Error loading data for date:', error);
      return null;
    }
  };

  /**
   * Wrapper for updateCell that intercepts date column changes
   * When entry_date column is changed, check database for existing data and load it if found
   */
  const handleCellChange = async (rowKey: string, columnKey: string, value: any) => {
    // If this is a date column change, check database and load data if exists
    if (columnKey === 'entry_date' && value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const rowIndex = rowKeys.indexOf(rowKey);
      if (rowIndex >= 0) {
        // Immediately update the cell value and row key to show the change right away
        updateCell(rowKey, columnKey, value);

        // Update row key immediately (don't wait for data loading)
        // This ensures the calendar shows the selected date immediately
        handleRowKeyChange(rowIndex, value);

        // Then check if data exists in database for this date (async, non-blocking)
        // Load data in the background and update if found
        // Use a small delay to ensure handleRowKeyChange has completed first
        setTimeout(() => {
          loadDataForDate(value).then(existingData => {
            if (existingData) {
              // Data exists - populate the row with existing data
              AMB_DEBUG && console.log(`📥 Found existing data for date ${value}, loading...`);

              // Map backend data to frontend format
              const mappedRow: any = {
                id: existingData.id || existingData.pk || undefined,
                entry_date: value,
                sunrise_time: existingData.sunrise_time || '',
                sunset_time: existingData.sunset_time || '',
                uptime_minutes: existingData.uptime_minutes ? String(existingData.uptime_minutes) : '',
                ghi_kwh_per_m2: existingData.ghi_kwh_per_m2 ? String(existingData.ghi_kwh_per_m2) : '',
                poa_kwh_per_m2: existingData.poa_kwh_per_m2 ? String(existingData.poa_kwh_per_m2) : '',
                avg_ambient_temp_c: existingData.avg_ambient_temp_c ? String(existingData.avg_ambient_temp_c) : '',
                max_ambient_temp_c: existingData.max_ambient_temp_c ? String(existingData.max_ambient_temp_c) : '',
                avg_module_temp_c: existingData.avg_module_temp_c ? String(existingData.avg_module_temp_c) : '',
                max_module_temp_c: existingData.max_module_temp_c ? String(existingData.max_module_temp_c) : '',
                avg_air_speed_ms: existingData.avg_air_speed_ms ? String(existingData.avg_air_speed_ms) : '',
                max_air_speed_ms: existingData.max_air_speed_ms ? String(existingData.max_air_speed_ms) : '',
                weather_status: existingData.weather_status || ''
              };

              // Update the row data with loaded data
              // Use the new key (value) since handleRowKeyChange has already updated it
              // Get current data from state (it will have the latest after handleRowKeyChange)
              const updatedData = { ...data };
              updatedData[value] = mappedRow;
              setAllData(updatedData, rowKeys);

              setStatus({
                type: 'success',
                message: `✅ Loaded existing data for ${value}`
              });
            } else {
              // No data exists - keep as empty row
              AMB_DEBUG && console.log(`📝 No existing data for date ${value}, keeping as empty row`);
            }
          }).catch(error => {
            console.error('Error loading data for date:', error);
          });
        }, 50); // Small delay to ensure handleRowKeyChange completes first

        return;
      }
    }

    // For all other columns, use normal updateCell
    updateCell(rowKey, columnKey, value);
  };

  /**
   * Handle row key (date) change
   */
  const handleRowKeyChange = (index: number, newKey: string) => {
    const oldKey = rowKeys[index];

    AMB_DEBUG && console.log(`🔄 Changing date from ${oldKey} to ${newKey}`);

    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newKey)) {
      setStatus({
        type: 'error',
        message: '❌ Invalid date format. Use YYYY-MM-DD.'
      });
      return;
    }

    // Check if new date already exists
    if (rowKeys.includes(newKey) && newKey !== oldKey) {
      setStatus({
        type: 'error',
        message: `❌ Date ${newKey} already exists. Please choose a different date.`
      });
      return;
    }

    // Update the date
    const updatedData = { ...data };
    // If data already exists for newKey (e.g., loaded from database), preserve it
    // Otherwise, copy from oldKey and update the entry_date field
    if (updatedData[newKey]) {
      // Data already exists for new key (likely loaded from database) - just ensure entry_date is correct
      updatedData[newKey] = { ...updatedData[newKey], entry_date: newKey };
    } else {
      // No data for new key - copy from old key
      updatedData[newKey] = { ...updatedData[oldKey], entry_date: newKey };
    }
    delete updatedData[oldKey];

    // Replace the old key with new key in the keys array and sort
    const keysAfterChange = rowKeys.map(k => k === oldKey ? newKey : k);
    const updatedKeys = [...keysAfterChange].sort();

    AMB_DEBUG && console.log('🔄 Reordering rows after date change:', {
      oldKey,
      newKey,
      oldIndex: index,
      beforeSort: keysAfterChange,
      afterSort: updatedKeys,
      newIndex: updatedKeys.indexOf(newKey)
    });

    isManuallySortingRef.current = true;

    // Create completely new references to ensure React detects the change
    const finalKeys = Array.from(updatedKeys); // Use Array.from to create new reference
    const finalData = { ...updatedData }; // Create new object reference

    // Update state using setTimeout to avoid flushSync during render
    setTimeout(() => {
      setRowKeysLocal(finalKeys);
      setTableKey(prev => prev + 1);
      setAllData(finalData, finalKeys);
    }, 0);

    // Update focused cell to the new position after reordering
    const newIndex = finalKeys.indexOf(newKey);
    if (newIndex >= 0 && newIndex !== index) {
      // Use useLayoutEffect timing for immediate update
      requestAnimationFrame(() => {
        setFocusedCell({ rowIndex: newIndex, colIndex: focusedCell.colIndex });
        scrollToCell(newIndex, focusedCell.colIndex);
      });
    }

    // Reset flag after state update completes
    setTimeout(() => {
      isManuallySortingRef.current = false;
    }, 100);

    AMB_DEBUG && console.log(`✅ Date changed successfully - row moved from index ${index} to ${updatedKeys.indexOf(newKey)}`);
  };

  /**
   * Save all ambient data to database
   */
  const save = async () => {
    setIsExporting(true);

    try {
      const entriesToSave: AmbientDataRow[] = [];

      AMB_DEBUG && console.log(`💾 Preparing to save ${rowKeys.length} rows...`);

      // Prepare data for batch save
      rowKeys.forEach(key => {
        const rowData = data[key];

        // Build ambient entry using column keys
        // Calculate uptime_minutes from sunrise/sunset if not provided
        let uptimeMinutes: number | null = null;

        // Helper function to parse time string (handles both 24-hour and 12-hour with AM/PM)
        const parseTimeToMinutes = (timeStr: string): number | null => {
          if (!timeStr || timeStr.trim() === '') return null;
          try {
            // Remove any whitespace
            timeStr = timeStr.trim().toUpperCase();

            // Check if it has AM/PM
            const hasAMPM = timeStr.includes('AM') || timeStr.includes('PM');

            if (hasAMPM) {
              // Handle 12-hour format with AM/PM
              const isPM = timeStr.includes('PM');
              timeStr = timeStr.replace(/AM|PM/gi, '').trim();
              const [hours, minutes = '0'] = timeStr.split(':');
              let h = parseInt(hours, 10);
              const m = parseInt(minutes, 10);

              if (isNaN(h) || isNaN(m)) return null;

              // Convert to 24-hour format
              if (isPM && h !== 12) h += 12;
              if (!isPM && h === 12) h = 0;

              return h * 60 + m;
            } else {
              // Handle 24-hour format
              const [hours, minutes = '0'] = timeStr.split(':');
              const h = parseInt(hours, 10);
              const m = parseInt(minutes, 10);

              if (isNaN(h) || isNaN(m)) return null;

              return h * 60 + m;
            }
          } catch {
            return null;
          }
        };

        // Try to use provided uptime_minutes first
        if (rowData['uptime_minutes'] && rowData['uptime_minutes'].toString().trim() !== '') {
          const parsed = parseInt(rowData['uptime_minutes'].toString().trim());
          uptimeMinutes = isNaN(parsed) ? null : parsed;
        }

        // If uptime_minutes not provided, calculate from sunrise/sunset
        if (uptimeMinutes === null && rowData['sunrise_time'] && rowData['sunset_time']) {
          const sunriseStr = rowData['sunrise_time'].toString().trim();
          const sunsetStr = rowData['sunset_time'].toString().trim();

          if (sunriseStr && sunsetStr) {
            const sunriseMinutes = parseTimeToMinutes(sunriseStr);
            const sunsetMinutes = parseTimeToMinutes(sunsetStr);

            if (sunriseMinutes !== null && sunsetMinutes !== null) {
              let calculated = sunsetMinutes - sunriseMinutes;

              // Handle case where sunset is next day (sunset < sunrise)
              if (calculated < 0) {
                // Assume sunset is next day, add 24 hours (1440 minutes)
                calculated = (1440 - sunriseMinutes) + sunsetMinutes;
              }

              // Only set if calculation is valid and positive
              uptimeMinutes = calculated > 0 ? calculated : null;
            }
          }
        }

        AMB_DEBUG && console.log(`🔢 Uptime calculation for ${key}:`, {
          sunrise: rowData['sunrise_time'],
          sunset: rowData['sunset_time'],
          calculated: uptimeMinutes
        });

        // Helper function to convert empty strings to null
        const cleanValue = (value: any): any => {
          if (value === null || value === undefined) return null;
          if (typeof value === 'string' && value.trim() === '') return null;
          return value;
        };

        // Helper function to safely parse float
        const parseFloatSafe = (value: any): number | null => {
          const cleaned = cleanValue(value);
          if (cleaned === null) return null;
          try {
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? null : parsed;
          } catch {
            return null;
          }
        };

        const entry: AmbientDataRow = {
          id: rowData['id'] ? Number(rowData['id']) : undefined,
          entry_date: key,
          site_code: siteCode, // Add site_code for backend
          sunrise_time: cleanValue(rowData['sunrise_time']),
          sunset_time: cleanValue(rowData['sunset_time']),
          uptime_minutes: uptimeMinutes,
          ghi_kwh_per_m2: parseFloatSafe(rowData['ghi_kwh_per_m2']),
          poa_kwh_per_m2: parseFloatSafe(rowData['poa_kwh_per_m2']),
          avg_ambient_temp_c: parseFloatSafe(rowData['avg_ambient_temp_c']),
          max_ambient_temp_c: parseFloatSafe(rowData['max_ambient_temp_c']),
          avg_module_temp_c: parseFloatSafe(rowData['avg_module_temp_c']),
          max_module_temp_c: parseFloatSafe(rowData['max_module_temp_c']),
          avg_air_speed_ms: parseFloatSafe(rowData['avg_air_speed_ms']),
          max_air_speed_ms: parseFloatSafe(rowData['max_air_speed_ms']),
          weather_status: cleanValue(rowData['weather_status'])
        };

        // Only save if at least one field has data AND the row is new (no ID from database)
        const hasData = Object.entries(entry).some(([k, val]) =>
          k !== 'entry_date' && val !== null && val !== undefined && val !== ''
        );

        // Only save new rows (rows without ID) - skip rows that are already in database
        const isNewRow = !entry.id || typeof entry.id !== 'number';

        if (hasData && isNewRow) {
          entriesToSave.push(entry);
          AMB_DEBUG && console.log(`✓ Row ${key} has data and is new, will be saved`);
        } else if (hasData && !isNewRow) {
          AMB_DEBUG && console.log(`⊘ Row ${key} already exists in database (ID: ${entry.id}), skipping save`);
        } else {
          AMB_DEBUG && console.log(`⊘ Row ${key} is empty, skipping`);
        }
      });

      if (entriesToSave.length === 0) {
        setStatus({
          type: 'warning',
          message: '⚠️ No data to save. Please enter some values.'
        });
        setIsExporting(false);
        return;
      }

      AMB_DEBUG && console.log(`💾 Saving ${entriesToSave.length} entries...`);

      let successCount = 0;
      let errorCount = 0;

      for (const entry of entriesToSave) {
        // Only save new entries (entries without ID) - entries with ID are already filtered out above
        let result = await ambientService.save(entry);

        if (result.ok) {
          successCount++;
          AMB_DEBUG && console.log(`✅ Saved: ${entry.entry_date}`);
        } else {
          errorCount++;
          console.error(`❌ Failed: ${entry.entry_date} - ${result.error}`);
        }
      }

      // Clear unsaved data from localStorage after successful save
      if (successCount > 0) {
        try {
          localStorage.removeItem(persistentStorageKey);
        } catch (error) {
          AMB_DEBUG && console.warn('Failed to clear saved data:', error);
        }
      }

      if (errorCount === 0) {
        setStatus({
          type: 'success',
          message: `✅ Successfully saved ${successCount} record${successCount > 1 ? 's' : ''}!`
        });
      } else {
        setStatus({
          type: 'warning',
          message: `⚠️ Saved ${successCount} record${successCount > 1 ? 's' : ''}, ${errorCount} failed.`
        });
      }

      // Reload saved data - use get-dgr for admins (all data), loadByUser for regular users
      if (successCount > 0) {
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const isAdmin = shouldLoadAllRecords(user, backendUser);
          const loadUserId = (resolvedUserId ?? Number(backendUser.id)) || 0;
          const savedDates = entriesToSave.map(e => e.entry_date);

          let loadedData: any = {};
          let allKeys: string[] = [];

          if (isAdmin) {
            // Admin: Load all data for the site using get-dgr endpoint
            try {
              const API_BASE_URL = 'http://localhost:8000/api/v1';
              const token = sessionStorage.getItem('access_token');
              const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Ambient`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const payload = await response.json();
                const ambientData = payload?.data?.ambient || [];

                ambientData.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    // Debug: Log weather_status value
                    if (row.weather_status) {
                      AMB_DEBUG && console.log(`🌤️ Found weather_status for ${dateKey}:`, row.weather_status);
                    }
                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined,
                      entry_date: dateKey,
                      sunrise_time: row.sunrise_time || '',
                      sunset_time: row.sunset_time || '',
                      uptime_minutes: row.uptime_minutes ? String(row.uptime_minutes) : '',
                      ghi_kwh_per_m2: row.ghi_kwh_per_m2 ? String(row.ghi_kwh_per_m2) : '',
                      poa_kwh_per_m2: row.poa_kwh_per_m2 ? String(row.poa_kwh_per_m2) : '',
                      avg_ambient_temp_c: row.avg_ambient_temp_c ? String(row.avg_ambient_temp_c) : '',
                      max_ambient_temp_c: row.max_ambient_temp_c ? String(row.max_ambient_temp_c) : '',
                      avg_module_temp_c: row.avg_module_temp_c ? String(row.avg_module_temp_c) : '',
                      max_module_temp_c: row.max_module_temp_c ? String(row.max_module_temp_c) : '',
                      avg_air_speed_ms: row.avg_air_speed_ms ? String(row.avg_air_speed_ms) : '',
                      max_air_speed_ms: row.max_air_speed_ms ? String(row.max_air_speed_ms) : '',
                      weather_status: row.weather_status ? String(row.weather_status).trim() : ''
                    };
                    // Add all dates (including today) to rowKeys
                    if (!allKeys.includes(dateKey)) {
                      allKeys.push(dateKey);
                    }
                  }
                });
              }
            } catch (error) {
              console.error('Error loading all data via get-dgr:', error);
              // Fallback to loadByUser if get-dgr fails
              const loadResult = await ambientService.loadByUser(loadUserId);
              if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
                const savedData = loadResult.data.filter((row: any) =>
                  savedDates.includes(row.entry_date)
                );

                savedData.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined,
                      entry_date: dateKey,
                      sunrise_time: row.sunrise_time || '',
                      sunset_time: row.sunset_time || '',
                      uptime_minutes: row.uptime_minutes ? String(row.uptime_minutes) : '',
                      ghi_kwh_per_m2: row.ghi_kwh_per_m2 ? String(row.ghi_kwh_per_m2) : '',
                      poa_kwh_per_m2: row.poa_kwh_per_m2 ? String(row.poa_kwh_per_m2) : '',
                      avg_ambient_temp_c: row.avg_ambient_temp_c ? String(row.avg_ambient_temp_c) : '',
                      max_ambient_temp_c: row.max_ambient_temp_c ? String(row.max_ambient_temp_c) : '',
                      avg_module_temp_c: row.avg_module_temp_c ? String(row.avg_module_temp_c) : '',
                      max_module_temp_c: row.max_module_temp_c ? String(row.max_module_temp_c) : '',
                      avg_air_speed_ms: row.avg_air_speed_ms ? String(row.avg_air_speed_ms) : '',
                      max_air_speed_ms: row.max_air_speed_ms ? String(row.max_air_speed_ms) : '',
                      weather_status: row.weather_status || ''
                    };
                    if (!allKeys.includes(dateKey)) {
                      allKeys.push(dateKey);
                    }
                  }
                });
              }
            }
          } else {
            // Regular user: Load only their data
            const loadResult = await ambientService.loadByUser(loadUserId);
            if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
              // Filter to only include the dates we just saved
              const savedData = loadResult.data.filter((row: any) =>
                savedDates.includes(row.entry_date)
              );

              savedData.forEach((row: any) => {
                const dateKey = row.entry_date;
                if (dateKey) {
                  loadedData[dateKey] = {
                    id: row.id || row.pk || undefined,
                    entry_date: dateKey,
                    sunrise_time: row.sunrise_time || '',
                    sunset_time: row.sunset_time || '',
                    uptime_minutes: row.uptime_minutes ? String(row.uptime_minutes) : '',
                    ghi_kwh_per_m2: row.ghi_kwh_per_m2 ? String(row.ghi_kwh_per_m2) : '',
                    poa_kwh_per_m2: row.poa_kwh_per_m2 ? String(row.poa_kwh_per_m2) : '',
                    avg_ambient_temp_c: row.avg_ambient_temp_c ? String(row.avg_ambient_temp_c) : '',
                    max_ambient_temp_c: row.max_ambient_temp_c ? String(row.max_ambient_temp_c) : '',
                    avg_module_temp_c: row.avg_module_temp_c ? String(row.avg_module_temp_c) : '',
                    max_module_temp_c: row.max_module_temp_c ? String(row.max_module_temp_c) : '',
                    avg_air_speed_ms: row.avg_air_speed_ms ? String(row.avg_air_speed_ms) : '',
                    max_air_speed_ms: row.max_air_speed_ms ? String(row.max_air_speed_ms) : '',
                    weather_status: row.weather_status || ''
                  };
                  if (!allKeys.includes(dateKey)) {
                    allKeys.push(dateKey);
                  }
                }
              });
            }
          }

          // Merge with existing data to preserve any unsaved changes
          if (Object.keys(loadedData).length > 0 || allKeys.length > 0) {
            // Sort all dates (ascending)
            allKeys.sort();

            // Filter out removed rows before merging
            const { data: filteredLoadedData, rowKeys: filteredLoadedKeys } = filterRemovedRows(
              loadedData,
              allKeys,
              'Ambient',
              siteCode,
              loadUserId
            );

            // Merge data - preserve local changes for weather_status if user just edited it
            const mergedData = { ...data };
            Object.keys(filteredLoadedData).forEach(dateKey => {
              const loadedRow = filteredLoadedData[dateKey];
              const existingRow = data[dateKey];

              // If row exists locally, merge carefully to preserve unsaved changes
              if (existingRow) {
                mergedData[dateKey] = {
                  ...loadedRow,
                  // Preserve local weather_status if it was recently changed (not empty and different from loaded)
                  weather_status: existingRow.weather_status && existingRow.weather_status !== ''
                    ? existingRow.weather_status
                    : (loadedRow.weather_status || '')
                };
              } else {
                mergedData[dateKey] = loadedRow;
              }
            });

            // Final rowKeys: all data sorted (already filtered)
            const finalKeys = [...filteredLoadedKeys];
            setAllData(mergedData, finalKeys);
          }
        }
      }

    } catch (error: any) {
      console.error('❌ Save error:', error);
      setStatus({
        type: 'error',
        message: `❌ Save failed: ${error.message}`
      });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Export data to CSV
   */
  const exportData = async () => {
    setIsExporting(true);

    try {
      await save();
      const result = await ambientService.export(rowKeys);

      if (result.ok) {
        setStatus({ type: 'success', message: '✅ Export completed successfully!' });
      } else {
        setStatus({ type: 'error', message: `❌ Export failed: ${result.error}` });
      }
    } catch (error: any) {
      console.error('❌ Export error:', error);
      setStatus({ type: 'error', message: `❌ Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Sort rows by date (ascending order)
   * Collects date values directly from frontend date inputs and sorts based on those values
   * 
   * IMPORTANT: This is FRONTEND-ONLY sorting. It only affects the display order.
   * - Does NOT call any backend APIs
   * - Does NOT modify backend data
   * - Does NOT affect save/load operations (data is saved/loaded based on row content, not order)
   * - Only reorders the visual display of rows in the table
   */
  const sortRowsByDate = () => {
    AMB_DEBUG && console.log('🔧 Manual sort triggered - collecting dates from frontend inputs');

    // Get the latest state values to ensure we have current data
    const currentRowKeys = [...rowKeys];
    const currentData = { ...data };

    if (currentRowKeys.length === 0) {
      setStatus({ type: 'warning', message: 'No rows to sort' });
      return;
    }

    // Create an array of rows with their actual date values from frontend inputs
    // Priority: 1) entry_date field in row data (from calendar input), 2) date from row key
    const rowsWithDates = currentRowKeys.map(key => {
      const rowData = currentData[key];

      // Get date from frontend input (entry_date column value) - this is the actual selected date
      let dateValue = '';
      if (rowData?.entry_date && /^\d{4}-\d{2}-\d{2}$/.test(String(rowData.entry_date))) {
        dateValue = String(rowData.entry_date);
      } else {
        // Fallback to using row key as date (rowKeys are dates in AmbientForm)
        dateValue = key;
      }

      return {
        key,
        date: dateValue,
        data: rowData
      };
    });

    AMB_DEBUG && console.log('📅 Collected dates from frontend:', rowsWithDates.map(r => ({
      key: r.key,
      date: r.date
    })));

    // Sort rows by date (ascending)
    rowsWithDates.sort((a, b) => {
      // Compare dates
      if (a.date !== b.date) {
        // Validate date format (YYYY-MM-DD) and use appropriate comparison
        if (/^\d{4}-\d{2}-\d{2}$/.test(a.date) && /^\d{4}-\d{2}-\d{2}$/.test(b.date)) {
          return a.date.localeCompare(b.date);
        }
        // Fallback to Date object comparison for other formats
        const dateAObj = new Date(a.date).getTime();
        const dateBObj = new Date(b.date).getTime();
        if (!isNaN(dateAObj) && !isNaN(dateBObj)) {
          return dateAObj - dateBObj;
        }
        // Final fallback to string comparison
        return a.date.localeCompare(b.date);
      }
      return 0; // Same date, maintain order
    });

    // Extract sorted keys
    const expectedKeys = rowsWithDates.map(row => row.key);

    // Check if already sorted
    const isAlreadySorted = JSON.stringify(expectedKeys) === JSON.stringify(currentRowKeys);
    if (isAlreadySorted) {
      setStatus({ type: 'info', message: 'Rows are already sorted by date' });
      return;
    }

    // Create new data object with sorted keys (preserve all data)
    const sortedData: any = {};
    expectedKeys.forEach(key => {
      if (currentData[key]) {
        sortedData[key] = currentData[key];
      }
    });

    AMB_DEBUG && console.log('🔄 Manual sort - reordering:', {
      before: currentRowKeys.map(k => {
        const row = rowsWithDates.find(r => r.key === k);
        return row ? row.date : k;
      }),
      after: expectedKeys.map(k => {
        const row = rowsWithDates.find(r => r.key === k);
        return row ? row.date : k;
      }),
      count: expectedKeys.length
    });

    // Set flag to prevent auto-sort from interfering
    isManuallySortingRef.current = true;

    // Update state in sequence to ensure proper re-render
    setTimeout(() => {
      // First update the data and rowKeys together
      setAllData(sortedData, expectedKeys);

      // Then update local state
      setRowKeysLocal(expectedKeys);

      // Force table re-render by incrementing tableKey
      setTableKey(prev => prev + 1);

      setTimeout(() => {
        isManuallySortingRef.current = false;
        setStatus({ type: 'success', message: `✅ Sorted ${expectedKeys.length} row(s) by date!` });
        AMB_DEBUG && console.log('✅ Sort completed - rows reordered by frontend date values:',
          expectedKeys.map(k => {
            const row = rowsWithDates.find(r => r.key === k);
            return row ? row.date : k;
          }).slice(0, 10)
        );
      }, 150);
    }, 0);
  };

  /**
   * Fetch and show summary modal
   */
  const fetchSummary = async () => {
    setLoadingSummary(true);

    try {
      const res = await ambientService.loadAll();

      if (res.ok && res.data) {
        setSummaryData(res.data);
        setShowSummary(true);
      } else {
        setStatus({ type: 'error', message: `❌ Failed to load summary: ${res.error}` });
      }
    } catch (error: any) {
      console.error('❌ Summary fetch error:', error);
      setStatus({ type: 'error', message: `❌ Failed to load summary: ${error.message}` });
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch user's data for Correct Mistake
  // Use already loaded data for Correct Mistake (optimization - no backend call)
  const handleCorrectMistake = () => {
    try {
      // Check if we have any data loaded
      if (rowKeys.length === 0) {
        setStatus({ type: 'warning', message: 'No data available to edit. Please add some data first.' });
        return;
      }

      // Convert current table data to array format for modal
      const dataArray = rowKeys.map(key => ({
        ...data[key],
        entry_date: key,
        id: data[key]?.id || undefined
      }));

      AMB_DEBUG && console.log('📋 Using loaded data for edit:', dataArray.length, 'rows');
      setCorrectMistakeData(dataArray);
      setShowCorrectMistake(true);
    } catch (error: any) {
      setStatus({ type: 'error', message: `Error: ${error.message}` });
    }
  };

  // Save corrected data - update all rows that have IDs
  const handleSaveCorrectedData = async (updatedData: AmbientDataRow[]) => {
    try {
      if (updatedData.length === 0) {
        throw new Error('No data to save');
      }

      const results: { success: number; failed: number; errors: string[] } = {
        success: 0,
        failed: 0,
        errors: []
      };

      // Process all rows that have IDs
      for (const row of updatedData) {
        if (row.id && typeof row.id === 'number') {
          try {
            AMB_DEBUG && console.log(`🔄 Updating Ambient record with ID: ${row.id}`);
            const updateResult = await ambientService.update(row.id, row);
            if (updateResult.ok) {
              results.success++;
              AMB_DEBUG && console.log(`✅ Updated Ambient data for ${row.entry_date} (ID: ${row.id})`);
            } else {
              results.failed++;
              results.errors.push(`Row ${row.id} (${row.entry_date}): ${updateResult.error || 'Update failed'}`);
            }
          } catch (error: any) {
            results.failed++;
            results.errors.push(`Row ${row.id} (${row.entry_date}): ${error.message}`);
          }
        } else {
          AMB_DEBUG && console.log(`⚠️ Skipping row without ID: ${row.entry_date}`);
        }
      }

      if (results.success > 0) {
        const message = results.failed > 0
          ? `✅ Updated ${results.success} record(s). ${results.failed} failed.`
          : `✅ Successfully updated ${results.success} record(s)!`;
        setStatus({ type: results.failed > 0 ? 'error' : 'success', message });
        if (results.errors.length > 0) {
          console.error('Update errors:', results.errors);
        }
      } else {
        throw new Error(`Failed to update any records. ${results.errors.join('; ')}`);
      }
    } catch (error: any) {
      throw new Error(`Failed to save: ${error.message}`);
    }
  };

  /**
   * Handle row deletion - shows dialog for permanent vs temporary removal
   */
  const handleRemoveRow = (index: number) => {
    const keyToRemove = rowKeys[index];
    setRowToDelete({ index, key: keyToRemove });
    setShowDeleteDialog(true);
  };

  /**
   * Handle permanent deletion - delete from database
   */
  const handlePermanentDelete = async () => {
    if (!rowToDelete) return;

    const keyToRemove = rowToDelete.key;
    const entryDate = keyToRemove; // For Ambient, key is the date

    try {
      setIsSaving(true);
      const deleteResult = await ambientService.delete(entryDate);

      if (deleteResult.ok) {
        // Remove from removed rows list if it was there
        backendApi.getCurrentUser().then(response => {
          if (response.success && response.user?.id) {
            const backendUser = response.user;
            const resolvedUserId = resolveUserFilterId(user, backendUser);
            const userIdForStore = resolvedUserId ?? Number(backendUser.id);
            removeRemovedRow('Ambient', siteCode, keyToRemove, userIdForStore);
          } else {
            removeRemovedRow('Ambient', siteCode, keyToRemove);
          }
        }).catch(() => {
          removeRemovedRow('Ambient', siteCode, keyToRemove);
        });

        // Remove the row from frontend state
        const updatedData = { ...data };
        delete updatedData[keyToRemove];
        const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort();

        setAllData(updatedData, updatedKeys);

        setStatus({
          type: 'success',
          message: `Permanently deleted ${keyToRemove} from database`
        });

        if (updatedKeys.length === 0) {
          setAllData({}, []);
        }
      } else {
        setStatus({
          type: 'error',
          message: `Failed to delete: ${deleteResult.error || 'Unknown error'}`
        });
      }
    } catch (error: any) {
      setStatus({
        type: 'error',
        message: `Failed to delete: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsSaving(false);
      setRowToDelete(null);
    }
  };

  /**
   * Handle temporary removal - hide until page refresh
   */
  const handleTemporaryRemove = () => {
    if (!rowToDelete) return;

    const keyToRemove = rowToDelete.key;

    // Add to removed rows list (persists across sheet switches)
    backendApi.getCurrentUser().then(response => {
      if (response.success && response.user?.id) {
        const backendUser = response.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForStore = resolvedUserId ?? Number(backendUser.id);
        addRemovedRow('Ambient', siteCode, keyToRemove, userIdForStore);
      } else {
        addRemovedRow('Ambient', siteCode, keyToRemove);
      }
    }).catch(() => {
      addRemovedRow('Ambient', siteCode, keyToRemove);
    });

    // Remove the row from frontend state only
    const updatedData = { ...data };
    delete updatedData[keyToRemove];
    const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort();

    setAllData(updatedData, updatedKeys);

    setStatus({
      type: 'info',
      message: `Temporarily hidden ${keyToRemove} (will reappear after page refresh)`
    });

    if (updatedKeys.length === 0) {
      setAllData({}, []);
    }

    setRowToDelete(null);
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="text-white px-3 pt-2 pb-0"
          style={{ background: 'linear-gradient(to right, var(--color-primary-blue), var(--color-secondary-tata-blue))' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud size={16} />
              <div>
                <h1 className="text-sm font-bold" style={{ color: '#ffffff', fontWeight: 700 }}><strong>Weather</strong> {siteName}</h1>

              </div>
            </div>
            <FormActions
              isCondensed={isCondensed}
              onToggleView={() => setIsCondensed(!isCondensed)}
              isExporting={isExporting}
              loadingSummary={loadingSummary}
              hasData={true}
              onAddRow={handleAddRow}
              onShowSummary={fetchSummary}
              onSave={save}
              onExport={exportData}
              addRowLabel="New Date"
              customActions={
                <>
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={(start, end) => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCorrectMistake}
                    disabled={loadingCorrectMistake}
                    className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--color-secondary-orange)' }}
                    title="Edit - View and edit your inserted data"
                  >
                    {loadingCorrectMistake ? (
                      <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Edit size={12} />
                    )}
                    <span className="hidden sm:inline">Edit</span>
                  </motion.button>
                </>
              }
            />
          </div>
        </div>

        {/* Status Banner */}
        {status.type && (
          <StatusBanner
            status={status.type}
            message={status.message}
            onClose={() => setStatus({ type: null, message: '' })}
          />
        )}

        {/* Table */}
        <div
          className="flex-1 min-h-0"
          style={{
            position: 'relative',
            scrollBehavior: 'auto' // Disable smooth scrolling to prevent animation
          }}
        >
          <UniversalTable
            columns={AMBIENT_COLUMNS}
            data={data}
            rowKeys={(startDate || endDate) ? filteredRowKeys : memoizedRowKeys}
            styleConfig={isCondensed ? DEFAULT_STYLE_CONFIG : EXPANDED_STYLE_CONFIG}
            focusedCell={focusedCell}
            onCellChange={handleCellChange}
            onRowKeyChange={handleRowKeyChange}
            onRemoveRowKey={handleRemoveRow}
            onKeyDown={handleKeyDown}
            onFocus={handleTableFocus}
            setInputRef={registerInput}
            containerRef={containerRef}
            loading={isInitialLoad}
          />
        </div>

        {/* Summary Modal */}
        {showSummary && (
          <SummaryModal
            isOpen={showSummary}
            onClose={() => setShowSummary(false)}
            title={`Weather Summary - ${siteName}`}
            data={summaryData}
            columns={[
              { key: 'entry_date', label: 'Date' },
              { key: 'sunrise_time', label: 'Sunrise' },
              { key: 'sunset_time', label: 'Sunset' },
              { key: 'ghi_kwh_per_m2', label: 'GHI (kWh/m²)', format: (v) => v ? Number(v).toFixed(2) : '-' },
              { key: 'poa_kwh_per_m2', label: 'POA (kWh/m²)', format: (v) => v ? Number(v).toFixed(2) : '-' },
              { key: 'avg_ambient_temp_c', label: 'Avg Temp (°C)', format: (v) => v ? Number(v).toFixed(1) : '-' },
              { key: 'weather_status', label: 'Weather' }
            ]}
          />
        )}

        {/* Footer */}
        <div className="px-4 py-2 text-xs border-t" style={{ background: 'var(--color-gray-100)', color: '#000000', fontWeight: 400 }}>
          <span>{footerText}</span>
        </div>

        {/* Correct Mistake Modal */}
        {showCorrectMistake && (
          <CorrectMistakeModal
            isOpen={showCorrectMistake}
            onClose={() => setShowCorrectMistake(false)}
            title="Edit - Weather"
            data={correctMistakeData}
            columns={AMBIENT_COLUMNS}
            onSave={handleSaveCorrectedData}
            isLoading={loadingCorrectMistake}
            siteCode="axon"
          />
        )}
      </div>

      {/* Delete Row Dialog */}
      <DeleteRowDialog
        isOpen={showDeleteDialog}
        rowKey={rowToDelete?.key || ''}
        onClose={() => {
          setShowDeleteDialog(false);
          setRowToDelete(null);
        }}
        onPermanent={handlePermanentDelete}
        onTemporary={handleTemporaryRemove}
      />
    </div>
  );
};

